import { type ComponentType } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import { useIqAllowed } from '@/shared/config/useIqAllowed';
import { GreetingV2 } from '../GreetingV2';
import { Springboard } from '../Springboard';
import type { SpringboardTabLabel } from '../Springboard/Springboard.types';
import { ToolShortcuts } from '../ToolShortcuts';
import { AssetReviews } from '../AssetReviews';
import { RecentProjects } from '../RecentProjects';
import { AudienceSparks } from '../AudienceSparks';
import type { AudienceSparksDeps } from '../AudienceSparks';
import { OpportunitiesPreview } from '../OpportunitiesPreview';
import type { OpportunitiesPreviewDeps } from '../OpportunitiesPreview';
import { StaggerItem } from '@/shared/ui/animations';

interface HomepageProps {
  /** User's name for the greeting (lifted from chats at page boundary). */
  userName: string | undefined;
  /** Customized springboard tab labels (lifted from springboard at page boundary). */
  springboardTabs: readonly SpringboardTabLabel[];
  /** IQ Analysis preview component, injected to avoid features→features import. */
  IQAnalysisPreview: ComponentType;
  /** Audience-sparks deps bag (Phase D — wired by the consuming page). */
  audienceSparks: AudienceSparksDeps;
  /** Opportunities-preview deps bag (Phase D — wired by the consuming page). */
  opportunitiesPreview: OpportunitiesPreviewDeps;
}

export const Homepage = ({
  userName,
  springboardTabs,
  IQAnalysisPreview,
  audienceSparks,
  opportunitiesPreview,
}: HomepageProps) => {
  // Without the gate's `isLoading`, unlike the tool surfaces: nothing reserves
  // this section's space, so holding it back would only delay it.
  const { isAllowed: isIqAllowed } = useIqAllowed();
  const projectsModuleEnabled = useBooleanFlagValue('projects_module', false);
  const assetApprovalsHidden = useAssetApprovalsHidden();
  const showAssetReviews = projectsModuleEnabled && !assetApprovalsHidden;

  return (
    <div className="flex h-full w-full overflow-y-auto">
      <div className="flex h-fit w-full flex-col gap-grid p-app-inset">
        {/* Staggered entrance (MFINS-3096): every home section cascades in on
            the shared 100ms rhythm via `StaggerItem`. The greeting opens the
            sequence internally (index 0 = title); the sections below continue
            it. Index ascends with visual position, so a section that moves in
            the layout takes the index its new position implies — the shortcuts
            sit beside the greeting and therefore precede the springboard.
            Conditional sections keep a fixed index so the cadence never drifts
            when one is hidden. */}
        <div className="flex gap-grid" data-testid="home-hero">
          <GreetingV2 userName={userName} />
          <StaggerItem index={2} className="w-1/2 min-w-0 shrink">
            <ToolShortcuts />
          </StaggerItem>
        </div>
        <StaggerItem index={3}>
          <Springboard tabs={springboardTabs} />
        </StaggerItem>
        {projectsModuleEnabled && (
          <StaggerItem index={4}>
            <RecentProjects />
          </StaggerItem>
        )}
        {showAssetReviews && (
          <StaggerItem index={5}>
            <AssetReviews />
          </StaggerItem>
        )}
        <StaggerItem index={6}>
          <AudienceSparks {...audienceSparks} />
        </StaggerItem>
        <StaggerItem index={7}>
          <OpportunitiesPreview {...opportunitiesPreview} />
        </StaggerItem>
        {isIqAllowed && (
          <StaggerItem index={8}>
            <IQAnalysisPreview />
          </StaggerItem>
        )}
      </div>
    </div>
  );
};
